import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';

const SolutionsXray = () => {
  return (
    <div className="min-h-screen flex flex-col bg-black text-white">
      <Navbar />
      <main className="flex-grow py-10 page-transition">
        <div className="container-custom">
          {/* Breadcrumb */}
          <div className="flex items-center text-sm mb-8">
            <Link to="/" className="text-gray-400 hover:text-purple-400">Home</Link>
            <span className="mx-2 text-gray-600">/</span>
            <Link to="/solutions" className="text-gray-400 hover:text-purple-400">Solutions</Link>
            <span className="mx-2 text-gray-600">/</span>
            <span className="text-purple-400">X-Ray Analysis</span>
          </div>
          
          {/* Hero Section */}
          <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
            <div>
              <h1 className="text-4xl font-bold mb-6">
                ZemedicAI Enterprise CXR
                <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-indigo-400">
                  AI that empowers clinicians
                </span>
              </h1>
              <p className="text-xl text-gray-300 mb-8">
                Developed by clinicians for clinicians, our AI decision support solution screens for 18 pathologies on chest X-rays in seconds.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/demo" className="btn-primary px-8 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-lg font-medium inline-block text-center">
                  Try Demo
                </Link>
                <Link to="/contact" className="px-8 py-3 bg-transparent border border-purple-500 text-purple-400 hover:bg-purple-600/10 rounded-lg font-medium inline-block text-center">
                  Download Brochure
                </Link>
              </div>
            </div>
            <div className="bg-gradient-to-br from-purple-900/20 to-indigo-900/20 rounded-xl overflow-hidden border border-gray-800">
              <img 
                src="/images/cxr-after.jpg" 
                alt="AI X-Ray Analysis" 
                className="w-full h-auto"
              />
            </div>
          </div>
          
          {/* Stats Section */}
          <div className="grid md:grid-cols-3 gap-8 mb-20">
            <div className="bg-gray-900 rounded-xl p-8 border border-gray-800 text-center">
              <div className="text-4xl font-bold text-purple-400 mb-2">18</div>
              <h3 className="text-xl font-semibold mb-4">Pathologies Screened</h3>
              <p className="text-gray-300">Every chest X-ray is screened for 18 clinically relevant pathology categories in one pass.</p>
            </div>
            <div className="bg-gray-900 rounded-xl p-8 border border-gray-800 text-center">
              <div className="text-4xl font-bold text-indigo-400 mb-2">Decision-Support</div>
              <h3 className="text-xl font-semibold mb-4">Not Diagnostic</h3>
              <p className="text-gray-300">Findings are flagged for clinician review — not a substitute for radiologist interpretation.</p>
            </div>
            <div className="bg-gray-900 rounded-xl p-8 border border-gray-800 text-center">
              <div className="text-4xl font-bold text-purple-400 mb-2">5</div>
              <h3 className="text-xl font-semibold mb-4">Partner Facilities</h3>
              <p className="text-gray-300">Closed clinical validation underway across five partner institutions in Nigeria and Kenya.</p>
            </div>
          </div>

          {/* Features Section */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold mb-12 text-center">Advanced Features</h2>
            
            <div className="grid md:grid-cols-2 gap-16 mb-16">
              <div>
                <h3 className="text-2xl font-semibold mb-6 flex items-center">
                  <div className="h-8 w-8 rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 flex items-center justify-center mr-3 flex-shrink-0">
                    <span className="text-white font-bold">1</span>
                  </div>
                  Intuitive Design
                </h3>
                <p className="text-gray-300 mb-4">
                  See the chest like never before with findings correlated across multiple views. Analyze up to three images per study.
                </p>
                <p className="text-gray-300 font-medium">
                  Results available in less than 20 seconds.
                </p>
                
                <div className="mt-8 bg-gray-900 rounded-xl overflow-hidden border border-gray-800">
                  <img 
                    src="https://plus.unsplash.com/premium_photo-1663047166207-f2be91530f28?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80" 
                    alt="Intuitive X-Ray Analysis Interface" 
                    className="w-full h-auto"
                  />
                </div>
              </div>
              
              <div>
                <h3 className="text-2xl font-semibold mb-6 flex items-center">
                  <div className="h-8 w-8 rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 flex items-center justify-center mr-3 flex-shrink-0">
                    <span className="text-white font-bold">2</span>
                  </div>
                  Seamless Workflow Integration
                </h3>
                <p className="text-gray-300 mb-4">
                  Our customisable user interface can be integrated effortlessly into your PACS and RIS. Most clinicians feel comfortable using ZemedicAI Enterprise CXR in under 10 studies.
                </p>
                <p className="text-gray-300">
                  To help clinicians interpret with confidence, a confidence bar displays the likelihood of the findings and the AI model's uncertainty.
                </p>
                
                <div className="mt-8 bg-gray-900 rounded-xl overflow-hidden border border-gray-800">
                  <img 
                    src="/images/cxr-after.jpg" 
                    alt="Seamless Workflow Integration" 
                    className="w-full h-auto"
                  />
                </div>
              </div>
            </div>
            
            <div className="text-center">
              <Link to="/contact" className="inline-block px-8 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-lg font-medium">
                Contact Us
              </Link>
            </div>
          </div>
          
          {/* Drafting the Future Section */}
          <div className="bg-gradient-to-br from-gray-900 to-gray-950 rounded-xl overflow-hidden border border-gray-800 mb-20">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="p-10">
                <h2 className="text-2xl font-bold mb-6">
                  Drafting the future of radiology with 
                  <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-indigo-400">
                    ZemedicAI Reporting
                  </span>
                </h2>
                <p className="text-gray-300 mb-4">
                  ZemedicAI Enterprise CXR is now available with our automated draft reporting feature.
                </p>
                <p className="text-gray-300 mb-4">
                  Designed to make radiology reporting quick and easy, ZemedicAI Reporting creates complete, editable draft reports ready for radiologists to review and sign off.
                </p>
                <p className="text-gray-300 mb-6">
                  ZemedicAI Reporting is fully integrated into the routine reporting workflow and backed by the comprehensive set of findings built into ZemedicAI solutions.
                </p>
                <Link to="#" className="text-purple-400 hover:text-purple-300 flex items-center font-medium">
                  Learn more about ZemedicAI Reporting
                  <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
                  </svg>
                </Link>
              </div>
              <div className="bg-gray-950">
                <img 
                  src="/images/cxr-after.jpg" 
                  alt="ZemedicAI Reporting" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
          
          {/* Clinical Robust AI Section */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold mb-12 text-center">
              Built on Real Clinical Validation
            </h2>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-gray-900 rounded-xl p-8 border border-gray-800">
                <div className="text-3xl font-bold text-purple-400 mb-4">1,000+</div>
                <h3 className="text-xl font-semibold mb-4">Images Processed</h3>
                <p className="text-gray-300">
                  Across chest X-ray, breast ultrasound, diabetic retinopathy, and diabetic foot ulcer modalities from African patient populations to date.
                </p>
              </div>

              <div className="bg-gray-900 rounded-xl p-8 border border-gray-800">
                <div className="text-3xl font-bold text-indigo-400 mb-4">80%</div>
                <h3 className="text-xl font-semibold mb-4">Clinician Agreement</h3>
                <p className="text-gray-300">
                  Agreement rate between ZemedicAI's screening output and independent clinical assessment, across a 200-case clinician-adjudicated sample at National Hospital, Abuja.
                </p>
              </div>

              <div className="bg-gray-900 rounded-xl p-8 border border-gray-800">
                <div className="text-3xl font-bold text-purple-400 mb-4">5</div>
                <h3 className="text-xl font-semibold mb-4">Partner Institutions</h3>
                <p className="text-gray-300">
                  Closed clinical validation across five hospitals and diagnostic facilities in Nigeria and Kenya.
                </p>
              </div>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="bg-gray-900 rounded-xl p-8 border border-gray-800 mb-20">
            <p className="text-gray-400 text-sm leading-relaxed">
              ZemedicAI Enterprise CXR is intended to assist clinicians with the interpretation of radiological imaging studies. It is not intended to replace clinical judgment or the expertise of qualified radiologists, and every result requires clinician sign-off before it is treated as confirmed.
            </p>
            <p className="text-gray-400 text-sm mt-4 leading-relaxed">
              For detailed device information, including indications for use, contraindications, and warnings, please consult the user guide prior to use. Features and availability may vary by region.
            </p>
            <p className="text-gray-400 text-sm mt-4 leading-relaxed">
              ZemedicAI, ZemedicAI Enterprise, and the ZemedicAI logo are trademarks of Zema AI Labs, registered in Nigeria.
            </p>
          </div>
          
          {/* CTA Section */}
          <div className="bg-gradient-to-r from-purple-900/50 to-indigo-900/50 rounded-2xl p-10 text-center border border-purple-500/30">
            <h2 className="text-3xl font-bold mb-4">Discover ZemedicAI Products</h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Contact us to request a brochure, private demonstration, or with any questions you may have.
            </p>
            <Link 
              to="/contact" 
              className="px-8 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-lg font-medium inline-block"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default SolutionsXray;